import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Modal, ModalBody, ModalContent, ModalFooter, ModalOverlay, Text, useToast } from '@chakra-ui/core';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { useSeekerContainerContext } from '../../SeekerContainerContext';

type UnblockModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const UnblockConfirmModal = ({ isOpen, onClose }: UnblockModalProps): JSX.Element => {
  const { t } = useTranslation('messaging');
  const toast = useToast();
  const { selectedCandidate } = useSeekerContainerContext();

  const accountId = useStoreState((s) => s.app.user?.account);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // istanbul ignore next
  const unblockCandidate = useStoreActions((actions) => actions.messaging.unblockCandidate);

  const handleUnblockRequest = async () => {
    if (!selectedCandidate || !accountId) return;

    setIsLoading(true);
    try {
      await unblockCandidate({
        accountId,
        candidateId: selectedCandidate.id,
      });
      toast({
        title: t('actions.unblock.unblockSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch (err) {
      console.error('Failed to unblock candidate: ', err);
      toast({
        title: t('actions.unblock.unblockError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered closeOnOverlayClick data-testid="unblock-info-modal">
      <ModalOverlay />
      <ModalContent px={2} py={3}>
        <ModalBody>
          <Heading fontWeight={700} fontSize="20px">
            {t('actions.unblock.modalHeader')}
          </Heading>
          <Text fontSize="14px" fontWeight={700} mt={3}>
            {t('actions.unblock.modalBody')}
          </Text>
        </ModalBody>
        <ModalFooter mt={5}>
          <Button colorScheme="red" onClick={onClose} mr={3} data-testid="cancel-btn">
            {t('actions.undismiss.confirmationModal.cancelButton')}
          </Button>
          <Button colorScheme="blue" isLoading={isLoading} data-testid="confirm-btn" onClick={handleUnblockRequest}>
            {t('actions.undismiss.confirmationModal.sendButton')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
